import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Modal, Portal, Card, Title, List, IconButton, useTheme, Divider, Text } from 'react-native-paper';
import { WaypointCategory } from '../services/waypointService';

interface WaypointCategoryModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSelectCategory: (category: WaypointCategory) => void;
  currentCategory?: WaypointCategory | null;
  showPrivateWaypoints?: boolean;
  onTogglePrivateWaypoints?: () => void;
}

export const WaypointCategoryModal: React.FC<WaypointCategoryModalProps> = ({
  visible,
  onDismiss,
  onSelectCategory,
  currentCategory,
  showPrivateWaypoints = false,
  onTogglePrivateWaypoints
}) => {
  const theme = useTheme();

  const categories = [
    {
      id: 'fishing' as WaypointCategory,
      title: 'Fishing Spots',
      description: 'Popular offshore fishing locations',
      icon: 'fish'
    },
    {
      id: 'wrecks' as WaypointCategory,
      title: 'Wrecks',
      description: 'Shipwrecks and underwater obstructions',
      icon: 'anchor'
    },
    {
      id: 'oilgas' as WaypointCategory,
      title: 'Oil/Gas Rigs',
      description: 'Offshore oil and gas platforms',
      icon: 'oil'
    }
  ];

  return (
    <Portal>
      <Modal 
        visible={visible} 
        onDismiss={onDismiss}
        contentContainerStyle={[styles.modalContent, { backgroundColor: theme.colors.surface }]}
      >
        <Card>
          <Card.Content>
            <View style={styles.header}>
              <Title style={styles.title}>Waypoints</Title>
              <IconButton
                icon="close"
                size={24}
                onPress={onDismiss}
                style={styles.closeButton}
              />
            </View>
            
            <Text style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}>
              Choose which waypoints to display on the map
            </Text>
            
            <Divider style={styles.divider} />
            
            <ScrollView style={styles.listContainer}>
              {/* Personal Waypoints Toggle */}
              {onTogglePrivateWaypoints && (
                <>
                  <List.Item
                    title="My Waypoints"
                    description="Your personal saved waypoints"
                    left={props => (
                      <List.Icon 
                        {...props} 
                        icon="account-key"
                        color={showPrivateWaypoints ? theme.colors.primary : undefined}
                      />
                    )}
                    right={props => 
                      showPrivateWaypoints ? (
                        <List.Icon {...props} icon="check" color={theme.colors.primary} />
                      ) : null
                    }
                    onPress={() => {
                      onTogglePrivateWaypoints();
                    }}
                    style={[
                      styles.listItem,
                      showPrivateWaypoints && { backgroundColor: theme.colors.primaryContainer }
                    ]}
                    titleStyle={showPrivateWaypoints ? { color: theme.colors.primary, fontWeight: '600' } : undefined}
                  />
                  <Divider />
                  <View style={styles.sectionHeader}>
                    <Text style={[styles.sectionTitle, { color: theme.colors.onSurfaceVariant }]}>
                      Public Waypoints
                    </Text>
                  </View>
                </>
              )}
              
              {/* Public Waypoint Categories */}
              {categories.map((category, index) => (
                <React.Fragment key={category.id}>
                  <List.Item
                    title={category.title}
                    description={category.description}
                    left={props => (
                      <List.Icon 
                        {...props} 
                        icon={category.icon}
                        color={currentCategory === category.id ? theme.colors.primary : undefined}
                      />
                    )}
                    right={props => 
                      currentCategory === category.id ? (
                        <List.Icon {...props} icon="check" color={theme.colors.primary} />
                      ) : null
                    }
                    onPress={() => {
                      onSelectCategory(category.id);
                      onDismiss();
                    }}
                    style={[
                      styles.listItem,
                      currentCategory === category.id && { backgroundColor: theme.colors.primaryContainer }
                    ]}
                    titleStyle={currentCategory === category.id ? { color: theme.colors.primary } : undefined}
                  />
                  {index < categories.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </ScrollView>
          </Card.Content>
        </Card>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    margin: 20,
    borderRadius: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  closeButton: {
    margin: -8,
  },
  divider: {
    marginBottom: 8,
  },
  listContainer: {
    maxHeight: 400,
  },
  listItem: {
    paddingVertical: 8,
    borderRadius: 8,
  },
  sectionHeader: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
});
